/**
 * ContactDataSectionPure
 *
 * Pure presentational component for the contact data tab.
 * Receives all data and callbacks via props instead of using tRPC hooks.
 */
import type { ReactNode } from 'react';
import { Fragment, useLayoutEffect, useRef, useState } from 'react';

import type { LinkedInMapping } from '../LinkedInIdentifiersSection';
import type { ContactEducationItem, ContactExperienceItem } from './ContactEmploymentEducationSectionPure';
import type { AddExperienceInput, OrgSearchOption } from './AddExperiencePopup';

type AddExperienceCallback = (input: AddExperienceInput) => Promise<void>;
type OrgSearchCallback = (query: string) => Promise<OrgSearchOption[]>;
import type { ContactSignalGridItem } from './ContactSignalsGrid';
import type { Contact, ContactFieldName, MeetingBookedData, PendingActionData } from './types';
import { cn } from '../cn';
import Button from '../Input2/Button';
import {
  ContactFieldAddRow,
  ContactFieldExpandedRows,
  ContactFieldReadOnly,
} from './ContactFieldReadOnly';
import { formatPhoneDisplay } from './formatPhoneDisplay';
import ToggleSwitch from '../Input2/ToggleSwitch';
import Tooltip from '../Input2/Tooltip';
import { LinkedInIdentifiersSectionPure } from '../LinkedInIdentifiersSection';
import { Scrollable } from '../Scrollable';
import { Arrows, Communication, General, Maps, Time, Users } from '../StrokeIcons';
import { CompactMetadataBar } from './CompactMetadataBar';
import { ContactEmploymentEducationSectionPure } from './ContactEmploymentEducationSectionPure';
import { ContactSignalsGrid } from './ContactSignalsGrid';

// ============================================================================
// Types
// ============================================================================

export type ContactDataSectionPureProps = {
  // Core data
  contact: Contact;
  authUserId: string;
  authIsPremium: boolean;
  authCreditsRemaining: number;
  authUnipileAccountId: string | null | undefined;
  authContactDisqualified: boolean;

  // Meeting booked data
  meetingBookedDetails: MeetingBookedData[] | null;
  pendingAction?: PendingActionData | null;

  // Flags
  hasSteps: boolean;
  hasCadence: boolean;

  // Mutation callbacks (from ContactDetailMutations)
  onToggleTracking: (tracked: boolean) => Promise<void>;
  onToggleDisqualified: (disqualified: boolean) => Promise<void>;
  onAddFieldValue: (field: ContactFieldName, value: string, source: string) => Promise<void>;
  onDeleteFieldValue: (field: ContactFieldName, source: string) => Promise<void>;
  onMakeFieldPrimary: (field: ContactFieldName, source: string) => Promise<void>;
  onRemoveFieldPrimary: (field: ContactFieldName, source: string) => Promise<void>;
  onVerifyEmail: (field: ContactFieldName, source: string) => Promise<void>;
  onRefreshFromLinkedIn: () => Promise<void>;
  onEnhanceContact: () => Promise<void>;
  onDeleteMeetingBooked: (interactionId: number) => Promise<void>;
  onAgeOverride: (minAge: number | null) => Promise<void>;
  onResetGender: () => Promise<void>;

  // Loading states
  isRefreshing: boolean;
  isEnhancing: boolean;
  isTrackingPending: boolean;
  isDisqualifiedPending: boolean;
  isAgeOverridePending: boolean;
  isResetGenderPending: boolean;

  // Toast callbacks for field copy operations
  onFieldCopied?: (data: string) => void;
  onFieldCopyFailed?: (data: string) => void;

  // LinkedIn Identifiers section data and callbacks
  linkedInMappings: LinkedInMapping[];
  linkedinUnavailable?: boolean;
  isLinkedInMappingsLoading: boolean;
  onDeleteLinkedInMapping: (mappingId: number) => void;
  onVerifyLinkedInMapping: (mappingId: number) => void;
  onCopyLinkedInMapping: (foreignId: string) => void;
  onAddLinkedInMapping: (foreignSource: 'linkedin' | 'salesnav', foreignId: string) => void;
  isLinkedInMappingDeleting: boolean;
  isLinkedInMappingVerifying: boolean;
  isLinkedInMappingAdding: boolean;

  // Optional superadmin copy feature
  onCopyToSuperadminTeam?: () => void;
  isCopyPending?: boolean;
  superadminName?: string;
  superadminTeamName?: string;

  // View all campaigns
  onViewAllCampaigns?: () => void;

  // View required actions
  onViewRequiredActions?: () => void;

  // Unipile connection callback (shown when not connected)
  onConnectUnipile?: () => void;

  // Tags section render prop
  renderContactTags?: () => React.ReactNode;

  // Employment & education history (rendered below the LinkedIn Identifiers section)
  experiences?: ContactExperienceItem[];
  education?: ContactEducationItem[];
  isHistoryLoading?: boolean;
  onAddExperience?: AddExperienceCallback;
  orgSearch?: OrgSearchCallback;
  onDeleteExperience?: (experienceId: string) => Promise<void>;

  // Signals (rendered as a 3x3 grid below the Track LinkedIn toggle when signals is provided)
  signals?: ContactSignalGridItem[];
  isSignalsLoading?: boolean;
  lastUnipileFetchAt?: Date | string | null;
  ignoredAspectKeys?: ReadonlySet<string>;

};

// ============================================================================
// ContactDataSectionPure
// ============================================================================

export function ContactDataSectionPure({
  contact,
  authUserId,
  // The Refresh & Enrich block moved to the tab row in ContactDetailPageCore. These props
  // are still in the type signature (caller passes them) but no longer used here.
  authIsPremium: _authIsPremium,
  authCreditsRemaining: _authCreditsRemaining,
  authUnipileAccountId: _authUnipileAccountId,
  authContactDisqualified,
  meetingBookedDetails,
  pendingAction,
  hasSteps,
  hasCadence,
  onToggleTracking,
  onToggleDisqualified,
  onAddFieldValue,
  onDeleteFieldValue,
  onMakeFieldPrimary,
  onRemoveFieldPrimary,
  onVerifyEmail,
  onRefreshFromLinkedIn: _onRefreshFromLinkedIn,
  onEnhanceContact: _onEnhanceContact,
  onDeleteMeetingBooked,
  onAgeOverride,
  onResetGender,
  isRefreshing: _isRefreshing,
  isEnhancing: _isEnhancing,
  isTrackingPending,
  isDisqualifiedPending,
  isAgeOverridePending,
  isResetGenderPending,
  onFieldCopied: _onFieldCopied,
  onFieldCopyFailed: _onFieldCopyFailed,
  linkedInMappings,
  linkedinUnavailable,
  isLinkedInMappingsLoading,
  onDeleteLinkedInMapping,
  onVerifyLinkedInMapping,
  onCopyLinkedInMapping,
  onAddLinkedInMapping,
  isLinkedInMappingDeleting,
  isLinkedInMappingVerifying,
  isLinkedInMappingAdding,
  onCopyToSuperadminTeam,
  isCopyPending,
  superadminName,
  superadminTeamName,
  onViewAllCampaigns,
  onViewRequiredActions,
  onConnectUnipile: _onConnectUnipile,
  renderContactTags,
  experiences,
  education,
  isHistoryLoading,
  onAddExperience,
  orgSearch,
  onDeleteExperience,
  signals,
  isSignalsLoading,
  lastUnipileFetchAt,
  ignoredAspectKeys,
}: ContactDataSectionPureProps) {
  const isTrackingButtonDisabled = hasSteps || hasCadence;
  const [openNameField, setOpenNameField] = useState<'firstName' | 'lastName' | null>(null);
  const [addingNameField, setAddingNameField] = useState<'firstName' | 'lastName' | null>(null);

  // If headline and title have the same primary value, collapse into one field
  const headlineValue = contact.primaryFields?.headline?.data?.trim();
  const titleValue = contact.primaryFields?.title?.data?.trim();
  const headlineTitleMatch = headlineValue && titleValue && headlineValue === titleValue;

  const fields: {
    name: ContactFieldName;
    placeholder: string;
    icon: ReactNode;
    value?: string;
    disabled?: boolean;
    allowVerify?: boolean;
  }[] = [
    { name: 'professionalEmail', placeholder: 'Professional Email', icon: <Communication.Mail01 />, allowVerify: true },
    { name: 'linkedinEmail', placeholder: 'Linkedin Email', icon: <Communication.Mail01 /> },
    { name: 'phone', placeholder: 'Phone', icon: <Communication.Phone /> },
    { name: 'firstName', placeholder: 'First Name', icon: <Users.User01 /> },
    { name: 'lastName', placeholder: 'Last Name', icon: <Users.User01 /> },
    {
      name: 'headline',
      placeholder: headlineTitleMatch ? 'Headline / Title' : 'Headline',
      icon: <General.Building02 />,
    },
    { name: 'cityFull', placeholder: 'Location', icon: <Maps.Map01 /> },
  ];

  // Add a divider after these fields
  const dividerAfter = new Set<ContactFieldName>(['phone']);

  // CompactMetadataBar values
  const probability =
    contact.liActiveUserProbability != null ? Math.round(contact.liActiveUserProbability * 100) : null;
  const age = contact.ageEstimate?.minAge ?? null;
  const hasAgeOverride = contact.ageEstimate?.source === 'override';
  const genderRaw = contact.primaryFields?.gender?.data;
  const gender: 'M' | 'F' | '?' = genderRaw === 'male' ? 'M' : genderRaw === 'female' ? 'F' : '?';
  const title = contact.primaryFields?.honorificTitle?.data ?? '—';

  const connectionsCount = contact.liConnectionsCount ?? 0;
  const recentPostsCount = contact.liRecentPostsCount ?? 0;
  const hasPicture = contact.primaryPictureUrl != null;
  const pictureContribution = hasPicture ? 20 : 0;
  const postsContribution = Math.min(40, recentPostsCount * 10);
  const connectionsContribution = Math.min(40, (connectionsCount / 500) * 40);

  const probabilityTooltip =
    probability != null ? (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className={hasPicture ? 'text-green-400' : 'text-gray-500'}>{hasPicture ? '✓' : '✗'}</span>
          <span>Profile picture</span>
          {hasPicture && <span className="text-green-400">+{pictureContribution}%</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className={recentPostsCount > 0 ? 'text-green-400' : 'text-gray-500'}>
            {recentPostsCount > 0 ? '✓' : '✗'}
          </span>
          <span>Recent posts: {recentPostsCount}</span>
          {recentPostsCount > 0 && <span className="text-green-400">+{Math.round(postsContribution)}%</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className={connectionsCount > 0 ? 'text-green-400' : 'text-gray-500'}>
            {connectionsCount > 0 ? '✓' : '✗'}
          </span>
          <span>Connections: {connectionsCount.toLocaleString()}</span>
          {connectionsCount > 0 && <span className="text-green-400">+{Math.round(connectionsContribution)}%</span>}
        </div>
      </div>
    ) : (
      'Research from LinkedIn to calculate'
    );

  const ageTooltip = hasAgeOverride
    ? 'Manually set — click pencil to edit'
    : age != null
      ? 'Calculated from work history — click pencil to override'
      : 'Unknown — research from LinkedIn';

  return (
    <Scrollable>
      <CompactMetadataBar
        probability={probability}
        age={age}
        gender={gender}
        title={title}
        probabilityTooltip={probabilityTooltip}
        ageTooltip={ageTooltip}
        hasAgeOverride={hasAgeOverride}
        onAgeOverride={onAgeOverride}
        isAgeOverridePending={isAgeOverridePending}
        onResetGender={onResetGender}
        isResetGenderPending={isResetGenderPending}
      />

      {/* Refresh & Enrich button moved to the tab row in ContactDetailPageCore (next to the
          zap/clock/person tabs and notes pencil) — kept compact and contextual to the page header
          rather than taking up a full body section. */}

      {fields.map((input) => {
        if (input.name === 'lastName') return null;

        if (input.name === 'firstName') {
          const lastNameField = fields.find((f) => f.name === 'lastName');
          const openValues =
            openNameField != null
              ? (contact.fields?.[openNameField] ?? []).filter((v) => !v.primary)
              : [];
          return (
            <Fragment key="name-pair">
              <div className="grid grid-cols-2 gap-0">
                <ContactFieldReadOnly
                  title={input.placeholder}
                  icon={input.icon}
                  fields={contact.fields?.[input.name]}
                  onRemovePrimary={(source) => onRemoveFieldPrimary(input.name, source)}
                  onMakePrimary={(source) => onMakeFieldPrimary(input.name, source)}
                  onDelete={(source) => onDeleteFieldValue(input.name, source)}
                  onAdd={(value) => onAddFieldValue(input.name, value, 'input')}
                  expanded={openNameField === 'firstName'}
                  onExpandedChange={(next) => {
                    setOpenNameField(next ? 'firstName' : null);
                    if (!next) setAddingNameField(null);
                  }}
                  isAdding={addingNameField === 'firstName'}
                  onIsAddingChange={(next) => {
                    setAddingNameField(next ? 'firstName' : null);
                    if (next) setOpenNameField('firstName');
                  }}
                  hideInlineExpandedPanel
                />
                {lastNameField && (
                  <ContactFieldReadOnly
                    title={lastNameField.placeholder}
                    icon={lastNameField.icon}
                    fields={contact.fields?.[lastNameField.name]}
                    onRemovePrimary={(source) => onRemoveFieldPrimary(lastNameField.name, source)}
                    onMakePrimary={(source) => onMakeFieldPrimary(lastNameField.name, source)}
                    onDelete={(source) => onDeleteFieldValue(lastNameField.name, source)}
                    onAdd={(value) => onAddFieldValue(lastNameField.name, value, 'input')}
                    expanded={openNameField === 'lastName'}
                    onExpandedChange={(next) => {
                      setOpenNameField(next ? 'lastName' : null);
                      if (!next) setAddingNameField(null);
                    }}
                    isAdding={addingNameField === 'lastName'}
                    onIsAddingChange={(next) => {
                      setAddingNameField(next ? 'lastName' : null);
                      if (next) setOpenNameField('lastName');
                    }}
                    hideInlineExpandedPanel
                  />
                )}
              </div>
              {openNameField != null && (openValues.length > 0 || addingNameField != null) && (
                <div
                  className={cn(
                    "relative isolate px-4 pt-0 pb-2 before:absolute before:inset-x-2 before:inset-y-0 before:-z-10 before:rounded-b-xl before:bg-slate-100 before:content-[''] dark:before:bg-slate-800/40",
                    openNameField === 'firstName' ? 'before:rounded-tr-xl' : 'before:rounded-tl-xl',
                  )}>
                  <ContactFieldExpandedRows
                    others={openValues}
                    onMakePrimary={(source) => onMakeFieldPrimary(openNameField, source)}
                    onDelete={(source) => onDeleteFieldValue(openNameField, source)}
                    addRow={
                      addingNameField === openNameField ? (
                        <ContactFieldAddRow
                          onSave={(value) => onAddFieldValue(openNameField, value, 'input')}
                          onCancel={() => setAddingNameField(null)}
                        />
                      ) : undefined
                    }
                  />
                </div>
              )}
            </Fragment>
          );
        }

        return (
          <Fragment key={input.name}>
            <ContactFieldReadOnly
              title={input.placeholder}
              icon={input.icon}
              fields={contact.fields?.[input.name]}
              onRemovePrimary={(source) => onRemoveFieldPrimary(input.name, source)}
              onMakePrimary={(source) => onMakeFieldPrimary(input.name, source)}
              onDelete={(source) => onDeleteFieldValue(input.name, source)}
              onAdd={(value) => onAddFieldValue(input.name, value, 'input')}
              onVerify={input.allowVerify ? (source) => onVerifyEmail(input.name, source) : undefined}
              formatValue={input.name === 'phone' ? formatPhoneDisplay : undefined}
            />
            {dividerAfter.has(input.name) && (
              <div className="mx-4 my-1 border-t border-gray-100 dark:border-gray-800" />
            )}
          </Fragment>
        );
      })}

      {/* Signals — moved high up because they're the most decision-critical info on the page.
          The dense LinkedIn Identifier rows used to live here but now live at the bottom (collapsed). */}
      {signals != null && (
        <ContactSignalsGrid
          signals={signals}
          isLoading={isSignalsLoading}
          lastUnipileFetchAt={lastUnipileFetchAt}
          ignoredAspectKeys={ignoredAspectKeys}
        />
      )}

      <ContactEmploymentEducationSectionPure
        experiences={experiences ?? []}
        education={education ?? []}
        isLoading={isHistoryLoading ?? false}
        onAddExperience={onAddExperience}
        orgSearch={orgSearch}
        onDeleteExperience={onDeleteExperience}
        contactFirstName={contact.primaryFields?.firstName?.data}
      />

      {renderContactTags && (
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">{renderContactTags()}</div>
      )}

      <div className="mt-1 flex flex-col gap-3 border-t border-gray-100 px-4 py-3 dark:border-gray-800">
        <div className="flex flex-col gap-4">
          <Tooltip tooltip="Mark this contact as disqualified to exclude them from future campaigns and analytics">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Disqualified</span>
              <ToggleSwitch
                size="sm"
                checked={authContactDisqualified}
                disabled={isDisqualifiedPending}
                onChange={onToggleDisqualified}
              />
            </div>
          </Tooltip>
          <Tooltip
            tooltip={
              isTrackingButtonDisabled
                ? 'Automatically enabled when contact is in an active campaign. LinkedIn activity is monitored to detect responses and engagement.'
                : "Enable to track this contact's LinkedIn activity and engagement outside of campaigns"
            }>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Track LinkedIn{isTrackingButtonDisabled ? ' (via campaign)' : ''}
              </span>
              <ToggleSwitch
                size="sm"
                checked={Boolean(isTrackingButtonDisabled || contact.tracked)}
                disabled={isTrackingButtonDisabled || isTrackingPending}
                onChange={onToggleTracking}
              />
            </div>
          </Tooltip>
          {meetingBookedDetails && meetingBookedDetails.length > 0 && (
            <MeetingBookedIndicator
              meetings={meetingBookedDetails}
              contactId={contact.id}
              currentUserId={authUserId}
              onDeleteMeetingBooked={onDeleteMeetingBooked}
              pendingAction={pendingAction}
            />
          )}
          {onCopyToSuperadminTeam && (
            <Button
              text
              icon={<General.Copy01 className="size-4" />}
              onClick={onCopyToSuperadminTeam}
              disabled={isCopyPending}
              tooltip={`Copy to ${superadminName ?? 'superadmin'}'s team${superadminTeamName ? ` (${superadminTeamName})` : ''}`}
              className="justify-start text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300">
              Copy to My Team
            </Button>
          )}
        </div>
      </div>

      {onViewAllCampaigns && (
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
          <Button
            text
            icon={<Time.ClockRewind className="size-4" />}
            onClick={onViewAllCampaigns}
            className="justify-start text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
            View Current & Past Campaigns
          </Button>
        </div>
      )}

      {onViewRequiredActions && (
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
          <Button
            text
            icon={<General.CheckCircle className="size-4" />}
            onClick={onViewRequiredActions}
            className="justify-start text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
            Actions Needed
          </Button>
        </div>
      )}

      {/* LinkedIn Identifiers — moved to the bottom and collapsed by default. Most users
          don't need to see URN IDs day-to-day; the section auto-expands if a mapping is failing. */}
      <LinkedInIdentifiersSectionPure
        mappings={linkedInMappings}
        linkedinUnavailable={linkedinUnavailable}
        isLoading={isLinkedInMappingsLoading}
        onDelete={onDeleteLinkedInMapping}
        onVerify={onVerifyLinkedInMapping}
        onCopy={onCopyLinkedInMapping}
        onAdd={onAddLinkedInMapping}
        isDeleting={isLinkedInMappingDeleting}
        isVerifying={isLinkedInMappingVerifying}
        isAdding={isLinkedInMappingAdding}
      />

    </Scrollable>
  );
}

// ============================================================================
// TriggerMessageCard
// ============================================================================

function formatShortDate(date: Date | string | null | undefined): string {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function TriggerMessageCard({ body, isSent, date }: { body: string; isSent: boolean; date: Date | null }) {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = body.length > 120;
  const displayBody = expanded || !needsTruncation ? body : body.slice(0, 120) + '...';

  return (
    <div
      onClick={needsTruncation ? () => setExpanded((v) => !v) : undefined}
      className={cn(
        'rounded-sm border p-2 border-blue-100 bg-blue-50/60 dark:border-blue-800 dark:bg-blue-900/10',
        needsTruncation && 'cursor-pointer hover:opacity-80',
      )}>
      <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400">
        {isSent && <Communication.Send01 className="size-3" />}
        {!isSent && <Arrows.CornerUpLeft className="size-3" />}
        <span>Triggered by {isSent ? 'sent' : 'received'} message</span>
        {date && <span className="text-slate-400 dark:text-slate-500">{formatShortDate(date)}</span>}
      </div>
      <p className="whitespace-pre-wrap text-xs text-slate-700 dark:text-slate-300">{displayBody}</p>
      {needsTruncation && !expanded && (
        <span className="mt-1 inline-block text-xs font-medium text-blue-500 dark:text-blue-400">
          Click to expand
        </span>
      )}
    </div>
  );
}

// ============================================================================
// MeetingBookedIndicator
// ============================================================================

type MeetingBookedIndicatorProps = {
  meetings: MeetingBookedData[];
  contactId: string;
  currentUserId: string;
  onDeleteMeetingBooked: (interactionId: number) => Promise<void>;
  pendingAction?: PendingActionData | null;
};

function MeetingBookedIndicator({
  meetings,
  contactId,
  currentUserId,
  onDeleteMeetingBooked,
  pendingAction,
}: MeetingBookedIndicatorProps) {
  const isSent = pendingAction?.sourceDirection === 'sent';

  return (
    <div className="flex flex-col gap-2 pt-1">
      <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
        <General.Zap className="size-4" />
        <span className="font-medium">Prospect interested</span>
      </div>
      {pendingAction && (
        <div className="flex flex-col gap-1.5 pl-6">
          <span className="text-xs font-medium text-slate-900 dark:text-slate-100">
            {pendingAction.actionText}
          </span>
          {pendingAction.sourceBody && (
            <TriggerMessageCard
              body={pendingAction.sourceBody}
              isSent={isSent}
              date={pendingAction.sourceDate}
            />
          )}
        </div>
      )}
      {meetings.map((meeting) => (
        <MeetingBookedItem
          key={meeting.interactionId}
          meeting={meeting}
          contactId={contactId}
          currentUserId={currentUserId}
          onDelete={onDeleteMeetingBooked}
        />
      ))}
    </div>
  );
}

// ============================================================================
// MeetingBookedItem
// ============================================================================

type MeetingBookedItemProps = {
  meeting: MeetingBookedData;
  contactId: string;
  currentUserId: string;
  onDelete: (interactionId: number) => Promise<void>;
};

function MeetingBookedItem({ meeting, currentUserId, onDelete }: MeetingBookedItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  const messageText = meeting.triggerMessage?.body || meeting.triggerMessage?.subject || '';
  const canDelete = meeting.userId === currentUserId;

  useLayoutEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [messageText]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(meeting.interactionId);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="group flex items-start justify-between gap-2 pl-6">
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-500 dark:text-gray-400">by {meeting.bookedByName}</p>
        {messageText && (
          <p
            ref={textRef}
            className={cn(
              'text-xs text-gray-400 dark:text-gray-500',
              !isExpanded && 'line-clamp-2',
              isTruncated && !isExpanded && 'cursor-pointer hover:text-gray-500 dark:hover:text-gray-400',
            )}
            onClick={isTruncated ? () => setIsExpanded(!isExpanded) : undefined}>
            "{messageText}"
          </p>
        )}
      </div>
      {canDelete && (
        <button
          onClick={handleDelete}
          disabled={isDeleting}
          className="shrink-0 text-gray-400 opacity-0 transition-opacity hover:text-red-500 disabled:opacity-50 group-hover:opacity-100"
          title="Remove interest detected">
          <General.Trash01 className="size-3" />
        </button>
      )}
    </div>
  );
}
